<template>
  <b-tab
    v-on:click="onClickTab"
    title="Outside IR35 Engagement via a Personal Services Company (PSC)"
  >
    <h2>Outside IR35 Engagement via a Personal Services Company (PSC)</h2>
    <transition name="fade">
      <b-row class="justify-content-end mb-3 togglers" v-if="showResults">
        <b-button variant="outline-dark" @click="expandAll(true)" class="small">Expand All</b-button>
        <b-button variant="outline-dark" @click="expandAll(false)" class="small">Collapse All</b-button>
      </b-row>
    </transition>
    <transition name="fade">
      <b-form @submit="onSubmit" v-if="showForm">
        <b-form-group
          id="input-group-calc-two-1"
          class="has-popup"
          label="Engagement Rate Per Day"
          label-cols="6"
          label-cols-md="7"
          label-for="input-calc-two-1"
          v-bind:description="prepopulateMessage"
        >
          <b-input-group prepend="£">
            <vue-numeric
              id="input-calc-two-1"
              type="text"
              placeholder="Enter Amount"
              class="form-control"
              ref="assignmentRate"
              separator=","
              precision="2"
              @focus="removePrepopulate"
              v-model="form.assignmentRate"
            ></vue-numeric>
            <b-input-group-append v-b-tooltip.hover title="Assuming you take holiday days">
              <b-input-group-text>
                <b-icon icon="question" variant="primary"></b-icon>
              </b-input-group-text>
              <b-input-group-text></b-input-group-text>
            </b-input-group-append>
          </b-input-group>
          <b-alert
            show
            variant="danger"
            v-if="$v.form.assignmentRate.$dirty && $v.form.assignmentRate.$error"
          >
            <div
              class="error"
              v-if="$v.form.assignmentRate.$dirty && !$v.form.assignmentRate.required"
            >This is required</div>
            <div
              class="error"
              v-if="$v.form.assignmentRate.$dirty && !$v.form.assignmentRate.between"
            >Please enter a number between 75 and 10000</div>
          </b-alert>
        </b-form-group>
        <b-form-group
          id="input-group-2"
          label="Days Worked"
          label-cols="6"
          label-cols-md="7"
          label-for="input-2"
          class="mb-4"
        >
          <b-input-group>
            <b-form-input
              id="input-2"
              v-model="form.daysWorked"
              required
              disabled
              placeholder="Enter Days"
            ></b-form-input>
            <b-input-group-append
              v-b-tooltip.hover
              title="Based on 260 working days minus 8 bank holiday days and 22 days holiday"
            >
              <b-input-group-text>
                <b-icon icon="question" variant="primary"></b-icon>
              </b-input-group-text>
              <b-input-group-text></b-input-group-text>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
        <h5 class="mt-5">Overheads</h5>
        <!-- Named expenses -->
        <b-form-group
          id="input-group-3-1"
          label="Computer Equipment"
          label-cols="6"
          label-cols-md="7"
          label-for="input-3-1"
        >
          <b-input-group prepend="£">
            <vue-numeric
              id="input-3-1"
              type="text"
              ref="computerEquipment"
              placeholder="Enter Amount"
              class="form-control"
              separator=","
              precision="2"
              v-model="namedExpenses.computerEquipment"
            ></vue-numeric>
          </b-input-group>
        </b-form-group>
        <b-form-group
          id="input-group-3-2"
          label="Professional Indemnity Insurance"
          label-cols="6"
          label-cols-md="7"
          label-for="input-3-2"
        >
          <b-input-group prepend="£">
            <vue-numeric
              id="input-3-2"
              type="text"
              ref="indemnityInsurance"
              placeholder="Enter Amount"
              class="form-control"
              separator=","
              precision="2"
              v-model="namedExpenses.indemnityInsurance"
            ></vue-numeric>
          </b-input-group>
        </b-form-group>
        <b-form-group
          id="input-group-3-3"
          label="IR35 Insurance"
          label-cols="6"
          label-cols-md="7"
          label-for="input-3-3"
        >
          <b-input-group prepend="£">
            <vue-numeric
              id="input-3-2"
              type="text"
              ref="ir35Insurance"
              placeholder="Enter Amount"
              class="form-control"
              separator=","
              precision="2"
              v-model="namedExpenses.ir35Insurance"
            ></vue-numeric>
          </b-input-group>
        </b-form-group>
        <b-form-group
          id="input-group-3-4"
          label="Employee &amp; Public Liability"
          label-cols="6"
          label-cols-md="7"
          label-for="input-3-4"
        >
          <b-input-group prepend="£">
            <vue-numeric
              id="input-3-2"
              type="text"
              ref="publicLiability"
              placeholder="Enter Amount"
              class="form-control"
              separator=","
              precision="2"
              v-model="namedExpenses.publicLiability"
            ></vue-numeric>
          </b-input-group>
        </b-form-group>
        <b-form-group
          id="input-group-3-5"
          label="Accounting Fees"
          label-cols="6"
          label-cols-md="7"
          label-for="input-3-5"
        >
          <b-input-group prepend="£">
            <vue-numeric
              id="input-3-2"
              type="text"
              ref="accountingFees"
              placeholder="Enter Amount"
              class="form-control"
              separator=","
              precision="2"
              v-model="namedExpenses.accountingFees"
            ></vue-numeric>
          </b-input-group>
        </b-form-group>
        <!-- Dynamic Expenses -->
        <div
          class="dynamic-expenses form-row form-group"
          v-for="(expense, key) in otherExpenses"
          v-bind:key="key"
        >
          <b-input-group class="col-6 col-md-7">
            <b-form-input
              v-model="expense.label"
              placeholder="Enter Other Expenses Description"
              class="text-left"
            ></b-form-input>
          </b-input-group>
          <b-input-group class="col-6 col-md-5" prepend="£">
            <vue-numeric
              type="text"
              placeholder="Enter Amount"
              class="form-control"
              separator=","
              precision="2"
              :ref="'otherExpense'+ key"
              v-model="expense.amount"
            ></vue-numeric>
            <b-input-group-append>
              <b-input-group-text>
                <b-icon
                  v-if="otherExpenses.length > 1"
                  @click="removeExpense(key)"
                  icon="trash-fill"
                  variant="secondary"
                ></b-icon>
                <b-icon v-else icon="trash-fill" variant="light"></b-icon>
                <b-icon
                  v-if="key + 1 === otherExpenses.length && (expense.amount || expense.label)"
                  @click="addExpense"
                  icon="plus"
                  variant="secondary"
                ></b-icon>
                <b-icon
                  v-if="key + 1 === otherExpenses.length && !(expense.amount || expense.label)"
                  icon="plus"
                  variant="light"
                ></b-icon>
                <b-icon
                  v-if="key + 1 !== otherExpenses.length && (expense.amount || expense.label)"
                  icon="plus"
                  variant="light"
                  class="disabled"
                ></b-icon>
              </b-input-group-text>
            </b-input-group-append>
          </b-input-group>
        </div>
        <h5 class="mt-5">Staff Costs</h5>
        <b-form-group
          id="input-group-4"
          label="Pension Contribution"
          label-cols="6"
          label-cols-md="7"
          label-for="input-4"
        >
          <b-input-group prepend="£">
            <vue-numeric
              id="input-4"
              type="text"
              ref="pension"
              precision="2"
              placeholder="Enter Amount"
              class="form-control"
              separator=","
              v-model="form.pension"
            ></vue-numeric>
          </b-input-group>
        </b-form-group>
        <b-form-group
          id="input-group-5"
          label="Contractor Gross Salary"
          label-cols="6"
          label-cols-md="7"
          label-for="input-5"
        >
          <b-input-group prepend="£">
            <vue-numeric
              id="input-5"
              type="text"
              ref="salary"
              precision="2"
              placeholder="Enter Amount"
              class="form-control"
              separator=","
              v-model="form.salary"
            ></vue-numeric>
          </b-input-group>
        </b-form-group>
        <b-form-group label="Add second employee?">
          <b-form-radio-group id="radio-group-1" v-model="secondEmployee" name="second-employee">
            <b-form-radio value="true">Yes</b-form-radio>
            <b-form-radio value="false">No</b-form-radio>
          </b-form-radio-group>
        </b-form-group>
        <b-form-group
          id="input-group-6"
          label="Other Salary (Partner/Company Secretary (1250L)"
          label-cols="6"
          label-cols-md="7"
          label-for="input-6"
          v-if="secondEmployee==='true'"
        >
          <b-input-group prepend="£">
            <vue-numeric
              id="input-6"
              type="text"
              precision="2"
              ref="otherSalary"
              placeholder="Enter Amount"
              class="form-control"
              separator=","
              v-model="form.secondSalary"
            ></vue-numeric>
          </b-input-group>
        </b-form-group>
        <b-alert
          :show="5"
          fade
          dismissible
          variant="danger"
          v-if="negativeEbit"
          @dismissed="negativeEbit=false"
        >The overheads exceed the revenue available, please reduce costs or increase the rate and try again.</b-alert>
        <b-button type="submit" variant="primary" v-if="showResults == false">Calculate</b-button>
      </b-form>
    </transition>
    <transition name="fade">
      <div v-if="showResults">
        <CalculatorTwoResults
          v-bind:results="results"
          v-bind:secondEmployee="secondEmployee"
          v-on:resetEdit="onResetEdit"
          v-on:incrementTab="onIncrementTab"
        />
      </div>
    </transition>
  </b-tab>
</template>

<script>
import CalculatorTwoResults from "@/components/CalculatorTwoResults";
import { resultFetcher } from "@/services/resultFetcher";
import { required, integer, minValue, between } from "vuelidate/lib/validators";

export default {
  props: ["tabIndex"],
  data() {
    return {
      form: {
        assignmentRate: null,
        daysWorked: 230,
        totalExpenses: null,
        pension: null,
        salary: null,
        secondSalary: null
      },
      secondEmployee: false,
      results: {},
      showForm: true,
      showResults: false,
      namedExpenses: {},
      otherExpenses: [],
      preventRemoval: true,
      prepopulateMessage: null,
      negativeEbit: false
    };
  },
  watch: {
    namedExpenses: {
      deep: true,
      handler() {
        this.sumExpenses();
      }
    },
    secondEmployee(isSelected) {
      if (isSelected == "false") {
        this.form.secondSalary = null;
      }
    },
    otherExpenses: {
      deep: true,
      handler(expenses) {
        this.preventRemoval = expenses.length <= 1;
        this.sumExpenses();
      }
    },
    revert(newValue) {
      if (newValue) {
        this.$store.commit("updateStoreValue", {
          key: "calcTwoRevert",
          value: false
        });
        this.showResults = false;
        this.clearForm();
        this.showForm = true;
      }
    },
    showResults() {
      // if toggled on, pageview on calc two results
      if (this.showResults) {
        this.$gtag.pageview({
          page_title: "Calculator Two Output",
          page_path: "/ir35-calculator-app/calculator-two-output"
        });
      }
      // if toggled off, pageview on calc two
      else {
        this.$gtag.pageview({
          page_title: "Calculator Two Input",
          page_path: "/ir35-calculator-app/calculator-two-input"
        });
      }
    },
    tabIndex() {
      // if tabindex is 1 and showresults is on, pageview results
      if (this.tabIndex == 1 && this.showResults) {
        this.$gtag.pageview({
          page_title: "Calculator Two Output",
          page_path: "/ir35-calculator-app/calculator-two-output"
        });
      } else if (this.tabIndex == 1 && !this.showResults) {
        // if tabindex is 1 and showresults is off, pageview input
        this.$gtag.pageview({
          page_title: "Calculator Two Input",
          page_path: "/ir35-calculator-app/calculator-two-input"
        });
      }
    }
  },
  computed: {
    revert() {
      return this.$store.state.calcTwoRevert;
    }
  },
  methods: {
    updatePrepopulate() {
      if (this.$store.state.prepopulate.two) {
        this.form.assignmentRate = this.$store.state.prepopulate.two;
        this.prepopulateMessage =
          "We've pre-filled this value based on your previous entry. Click to edit.";
      }
    },
    async clearForm() {
      this.form.totalExpenses = null;
      this.form.pension = null;
      this.form.salary = null;
      this.form.secondSalary = null;
      this.secondEmployee = false;
      this.namedExpenses = {};
      this.otherExpenses = [];
      await this.$refs.assignmentRate;
      // this is a hack to get round the focus behaviour
      let prepopulateTemp = this.$store.state.prepopulate.two;
      this.$refs.assignmentRate.$el.focus();
      this.$refs.assignmentRate.$el.blur();
      this.$store.commit("prepopulateField", {
        calculator: "two",
        value: prepopulateTemp
      });
      this.addExpense();
      this.sumExpenses();
    },
    removePrepopulate() {
      // if user clicks to edit the field, then the prepopulation is deleted
      this.prepopulateMessage = null;
      this.$store.commit("prepopulateField", {
        calculator: "two",
        value: null
      });
    },
    sumExpenses() {
      // calculate total expenses for submission
      let totalExpenses = 0;
      let namedExpenses = this.namedExpenses;
      Object.keys(namedExpenses).forEach(function(key) {
        totalExpenses += parseFloat(namedExpenses[key]);
      });
      this.otherExpenses.forEach(function(expense) {
        // check it's a number first
        if (!isNaN(parseFloat(expense.amount))) {
          totalExpenses += parseFloat(expense.amount);
        }
      });
      this.form.totalExpenses = totalExpenses;
    },
    storeExpenses() {
      // save the split expenses to vuex so we can use them in the results page
      this.$store.commit("updateStoreValue", {
        key: "namedExpenses",
        value: this.namedExpenses
      });
      this.$store.commit("updateStoreValue", {
        key: "otherExpenses",
        value: this.otherExpenses
      });
    },
    onResetEdit: async function() {
      this.showForm = true;
      this.showResults = false;
      // hacky workaround for vue numeric bug
      // fixed refs
      await this.$refs.assignmentRate;
      this.$refs.assignmentRate.$el.focus();
      this.$refs.assignmentRate.$el.blur();
      await this.$refs.indemnityInsurance;
      this.$refs.indemnityInsurance.$el.focus();
      this.$refs.indemnityInsurance.$el.blur();
      await this.$refs.ir35Insurance;
      this.$refs.ir35Insurance.$el.focus();
      this.$refs.ir35Insurance.$el.blur();
      await this.$refs.computerEquipment;
      this.$refs.computerEquipment.$el.focus();
      this.$refs.computerEquipment.$el.blur();
      await this.$refs.publicLiability;
      this.$refs.publicLiability.$el.focus();
      this.$refs.publicLiability.$el.blur();
      await this.$refs.accountingFees;
      this.$refs.accountingFees.$el.focus();
      this.$refs.accountingFees.$el.blur();
      await this.$refs.pension;
      this.$refs.pension.$el.focus();
      this.$refs.pension.$el.blur();
      await this.$refs.salary;
      this.$refs.salary.$el.focus();
      this.$refs.salary.$el.blur();
      // dynamic refs
      let that = this.$refs;
      this.otherExpenses.forEach(async function(expense, key) {
        await that["otherExpense" + key][0];
        that["otherExpense" + key][0].$el.focus();
        that["otherExpense" + key][0].$el.blur();
      });
      // if ref
      if (this.secondEmployee) {
        await this.$refs.otherSalary;
        this.$refs.otherSalary.$el.focus();
        this.$refs.otherSalary.$el.blur();
      }
    },
    async onSubmit(e) {
      e.preventDefault();
      this.$v.form.$touch();
      if (this.$v.form.$error) {
        return;
      }
      // form submit after this
      this.results = await resultFetcher.calculatorTwo(this.form);
      if (this.results.error == "Negative EBIT") {
        this.negativeEbit = true;
        return;
      }
      this.storeExpenses();
      this.showResults = true;
      this.showForm = false;
    },
    expandAll(value) {
      if (value) {
        this.$store.commit("expandCollapseAll", {
          calculator: "two",
          action: "expand"
        });
      } else {
        this.$store.commit("expandCollapseAll", {
          calculator: "two",
          action: "collapse"
        });
      }
    },
    addExpense() {
      let checkEmpty = this.otherExpenses.filter(
        expense => expense.amount === null && expense.label === null
      );
      if (checkEmpty.length >= 1 && this.otherExpenses.length > 0) {
        return;
      }
      this.otherExpenses.push({
        label: null,
        amount: null
      });
    },
    removeExpense(key) {
      if (!this.preventRemoval) {
        this.otherExpenses.splice(key, 1);
      }
    },
    onIncrementTab() {
      // tell calc three to revert to input
      this.$store.commit("updateStoreValue", {
        key: "calcThreeRevert",
        value: true
      });
      // pass the event up the chain
      this.$emit("incrementTab");
    },
    onClickTab: function() {
      // if we've come to this via a click remove the prepopulation
      this.removePrepopulate();
    }
  },
  mounted() {
    this.addExpense();
    this.sumExpenses();
  },
  created() {
    this.$store.subscribe(mutation => {
      if (mutation.type === "prepopulateField") {
        this.updatePrepopulate();
      }
    });
  },
  components: {
    CalculatorTwoResults
  },
  validations: {
    form: {
      assignmentRate: {
        required,
        between: between(75, 10000)
      },
      daysWorked: {
        required,
        integer,
        positive: minValue(0),
        between: between(1, 365)
      }
    }
  }
};
</script>
